import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { useBloc } from "@blac/react";
import { FC, ReactNode, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { DropZone } from "react-aria-components";
import { XCrossIcon } from "../../constants/Icon";
import CenterEllipsisText from "../../atom/text/CenterEllipsisText";
import SwipeDismissTransition from "../../atom/animation/SwipeDismissTransition";
import DocumentIcon from "../../icons/DocumentIcon.svg?react";
import FileUploadCubit from "./FileUploadCubit";
import type { FileUploadStatus } from "./FileUploadCubit";

export interface FileUploadBoxProps {
  instanceId: string;
  title: ReactNode;
  children?: ReactNode;

  isLoading?: boolean;

  uploadLabel?: ReactNode;
  loadingLabel?: ReactNode;
  helperText?: ReactNode;
}

const Container = styled.div`
  label: FUBContainer;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  border-radius: 0.375rem;
  border-left: 4px solid var(--Accessible-Blue, #3973da);
  background: rgba(171, 212, 255, 0.24);
  text-align: left;
  min-width: 0;
  overflow: hidden;
`;

const Title = styled.div`
  font-size: 1.1rem;
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: -0.02em;
  color: var(--Greys-Charcoal, #212121);
`;

const Body = styled.div`
  font-size: 0.9375rem;
  line-height: 1.5;
  color: var(--Greys-Charcoal, #212121);
`;

const DropArea = styled.label`
  label: FUBDropArea;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  border: 2px dashed var(--Accessible-Blue, #3973da);
  border-radius: 0.8rem;
  background: transparent;
  transition: background 0.15s ease;
  cursor: pointer;

  &:hover {
    background: rgba(171, 212, 255, 0.4);
  }

  &:focus-within {
    outline: 2px solid var(--Accessible-Blue, #3973da);
    outline-offset: 2px;
  }

  &[data-highlighted="true"] {
    background: rgba(171, 212, 255, 0.4);
  }
`;

const HiddenFileInput = styled.input`
  width: 0;
  height: 0;
  opacity: 0.001;
  overflow: hidden;
  position: absolute;
`;

const UploadContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  color: var(--Accessible-Blue, #3973da);
  font-size: 0.9375rem;
  font-weight: 500;

  span[aria-hidden="true"] {
    font-size: 1rem;
    line-height: 0;
  }
}
`;

const FileCard = styled.div`
  label: FUBFileCard;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1rem 0.9rem;
  border-radius: 0.8rem;
  background: var(--Greys-White, #fff);
  border: 2px solid transparent;
  color: var(--Greys-Charcoal, #212121);

  &[data-error="true"] {
    color: var(--primary-warning-red, #bf1902);
  }
`;

const FileInfo = styled.div`
  min-width: 0;
`;

const FileName = styled(CenterEllipsisText)`
  label: FUBFileName;
  color: inherit;
  font-size: 0.9375rem;
  font-style: normal;
  font-weight: 400;
  line-height: 140%; /* 19.6px */
  letter-spacing: -0.28px;
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  margin: -0.5rem -0.5rem -0.5rem 0;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  color: inherit;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }

  &:focus-visible {
    outline: 2px solid var(--Accessible-Blue, #3973da);
    outline-offset: 2px;
  }
`;

const ErrorFileCard = styled(FileCard)`
  border-color: var(--primary-warning-red, #bf1902);
  background: #fff;
  color: inehrit;
`;

const ErrorMessage = styled.div`
  font-size: 0.8125rem;
  color: var(--primary-warning-red, #bf1902);
  line-height: 1.4;
`;

const HelperText = styled.div`
  font-size: 0.8125rem;
  color: var(--Greys-Dark-Grey, #757575);
  line-height: 1.4;
`;

const FilesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &[data-empty="true"] {
    display: none;
  }
`;

const FileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid rgba(57, 115, 218, 0.2);
  border-top-color: var(--Accessible-Blue, #3973da);
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
  flex-shrink: 0;
`;

const StatusIndicator: FC<{ status: FileUploadStatus }> = ({ status }) => {
  if (status === "uploading") {
    return <Spinner />;
  }
  return null;
};

const FileUploadBox: FC<FileUploadBoxProps> = ({
  instanceId,
  title,
  children,
  isLoading = false,
  uploadLabel = "Upload file",
  loadingLabel = "Loading...",
  helperText
}) => {
  const [state, cubit] = useBloc(FileUploadCubit, { id: instanceId });
  const { files, fileErrors, fileStatuses } = state;

  const inputRef = useRef<HTMLInputElement>(null);
  cubit.setRefs({ inputRef });

  const acceptAttr = cubit.accept !== "*" ? cubit.accept : undefined;

  return (
    <Container>
      <Title>{title}</Title>
      {children && <Body>{children}</Body>}

      <FilesContainer data-empty={files.length === 0 ? "true" : undefined}>
        <AnimatePresence initial={false}>
          {files.map((file, index) => {
            const error = fileErrors?.get(index);
            const status = fileStatuses?.get(index);
            const Card = error ? ErrorFileCard : FileCard;
            const fileKey = `${file.name}-${file.size}-${file.lastModified}`;

            return (
              <SwipeDismissTransition key={fileKey}>
                <Card data-error={error ? "true" : undefined}>
                  <DocumentIcon />
                  <FileInfo>
                    <FileName text={file.name} />
                    {error?.message && (
                      <ErrorMessage>{error.message}</ErrorMessage>
                    )}
                  </FileInfo>
                  <FileActions>
                    {status && !error && <StatusIndicator status={status} />}
                    <RemoveButton
                      type="button"
                      onClick={() => cubit.handleRemove(file, index)}
                      aria-label={`Remove ${file.name}`}
                    >
                      <XCrossIcon />
                    </RemoveButton>
                  </FileActions>
                </Card>
              </SwipeDismissTransition>
            );
          })}
        </AnimatePresence>
      </FilesContainer>

      {cubit.showDropzone && (
        <DropZone onDrop={cubit.handleDrop}>
          {({ isDropTarget }) => (
            <DropArea
              data-highlighted={isDropTarget || isLoading ? "true" : undefined}
            >
              <HiddenFileInput
                ref={inputRef}
                type="file"
                accept={acceptAttr}
                multiple={cubit.multiple}
                onChange={cubit.handleFileSelect}
              />
              <UploadContent>
                {isLoading ? (
                  <>{loadingLabel}</>
                ) : (
                  <>
                    <span aria-hidden>+</span>
                    {uploadLabel}
                  </>
                )}
              </UploadContent>
            </DropArea>
          )}
        </DropZone>
      )}

      {helperText && <HelperText>{helperText}</HelperText>}
    </Container>
  );
};

export default FileUploadBox;
